import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  type KmEntityType,
  useCreateKmEntityType,
  useDeleteKmEntityType,
  useUpdateKmEntityType,
} from '@/entities/ontology';
import { useHasPermission } from '@/entities/profile';
import { useFormGuardActions } from '@/entities/formGuard';
import { useAppNavigate } from '@/shared/lib/ts-router';
import type { OntologyFormValues } from './types';
import { useOntologyTypeSchema } from './validation';

type Params = {
  initial?: KmEntityType;
};

/**
 * Логика формы типа сущности: инициализация, сохранение (создание/изменение),
 * инлайн-сохранение названия и удаление. Определяет режим только для чтения.
 */
export const useOntologyForm = ({ initial }: Params) => {
  const navigate = useAppNavigate();
  const canAdmin = useHasPermission('ADMIN_KM_ENTITY_TYPE');
  const schema = useOntologyTypeSchema();
  const { resetFormGuard } = useFormGuardActions();

  const isCreate = !initial;
  const isEdit = !!initial;
  // Только для чтения: нет права администрирования, либо системный/удалённый тип
  const isDisabled = !canAdmin || !!initial?.isSystem || !!initial?.isDeleted;

  const defaults: OntologyFormValues = useMemo(
    () => ({
      code: initial?.code ?? '',
      name: initial?.name ?? '',
      description: initial?.description ?? '',
    }),
    [initial],
  );

  const form = useForm<OntologyFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset(defaults);
  }, [form, defaults]);

  const { mutateAsync: createType, isPending: isCreating } = useCreateKmEntityType();
  const { mutateAsync: updateType, isPending: isUpdating } = useUpdateKmEntityType();
  const { mutateAsync: deleteType, isPending: isDeleting } = useDeleteKmEntityType();
  const isLoading = isCreating || isUpdating || isDeleting;

  const onSubmit = form.handleSubmit(async (values) => {
    if (isDisabled) {
      return;
    }

    if (isEdit) {
      await updateType({
        code: initial!.code,
        payload: { name: values.name, description: values.description || null },
      });
      resetFormGuard();
      return;
    }

    const created = await createType({
      code: values.code,
      name: values.name,
      description: values.description || null,
    });
    // Снимаем признак несохранённых изменений и уходим на карточку созданного типа
    // через безопасную навигацию проекта: она временно разрешает переход и обходит
    // глобальный form-guard (обычный navigate здесь блокируется диалогом)
    resetFormGuard();
    navigate('/ontology/:code', { code: created.code }, { replace: true });
  });

  // Инлайн-сохранение названия из EditableTitle при потере фокуса
  const handleTitleSave = async (value: string) => {
    form.setValue('name', value);

    if (isEdit && !isDisabled && value !== initial?.name) {
      await updateType({ code: initial!.code, payload: { name: value } });
    }
  };

  const handleDelete = async () => {
    if (isEdit && !isDisabled) {
      await deleteType(initial!.code);
    }
  };

  return {
    form,
    isCreate,
    isEdit,
    isDisabled,
    isLoading,
    canAdmin,
    onSubmit,
    handleTitleSave,
    handleDelete,
  } as const;
};
